import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import useTurnstileChallenge from '../lib/useTurnstileChallenge';
import toast from 'react-hot-toast';
import { OTP_TEXT, otpChannelLabel, otpChannelOf, otpSentText, postJsonWithOtpCapability, useOtpDelivery, useSecondsUntil } from '../lib/otpChannel';
import { TWO_FACTOR_TEXT, resendExtra, twoFactorFromError, twoFactorHint } from '../lib/loginTwoFactor';

// Cloudflare-Turnstile-Site-Key (öffentlich). Ist er gesetzt, erscheint die Bot-Challenge.
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const API = process.env.REACT_APP_API_URL || 'https://localhost:5001/api';

// Eigener fetch (umgeht einen evtl. globalen 401-Interceptor).
// WHATSAPP-OTP-STANDARD-001: Code-auslösende Pfade tragen das Token (Liste + Sicherheitsnetz in
// lib/otpChannel.js); `channel` nur aus Antworten, deren Anfrage es trug.
async function sendJson(path, body) {
  const controller = new AbortController();
  const deadline = setTimeout(() => controller.abort(), 15000);
  try {
    const { response, data, advertised } = await postJsonWithOtpCapability({
      fetchImpl: (url, options) => fetch(url, { ...options, signal: controller.signal }),
      base: API, path, body, headers: { 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
    });
    if (controller.signal.aborted) throw new Error('timeout');
    if (!response.ok) throw new Error(data.error || data.message || 'Fehler');
    return { data, channel: otpChannelOf(data, advertised) };
  } catch (error) {
    if (controller.signal.aborted) throw new Error('Keine Antwort bestätigt. Prüfe erhaltene Codes oder deinen aktuellen Kontostand, bevor du bewusst erneut versuchst.');
    throw error;
  } finally { clearTimeout(deadline); }
}

async function postJson(path, body) {
  return (await sendJson(path, body)).data;
}

const inputStyle = { width: '100%', padding: '11px 13px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, color: '#111', boxSizing: 'border-box' };
// Lesbarkeit: gesperrt bleibt der Text voll deckend (#6b7280 ≥ 4,5:1 auf der hellen Karte) — die globale
// Regel button:disabled { opacity:.5 } würde die Restzeit unter den Kontrast drücken.
const linkButton = (enabled) => ({ background: 'none', border: 'none', padding: 0, color: enabled ? '#4b5563' : '#6b7280', opacity: 1, fontSize: 13, cursor: enabled ? 'pointer' : 'not-allowed', textDecoration: enabled ? 'underline' : 'none' });

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 700, color: '#4b5563', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 };

export default function Login() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [mode, setMode] = useState('login');   // 'login' | 'forgot'
  const { partnerLogin } = useAuth();
  const navigate = useNavigate();
  const attemptRef = useRef(0);
  useEffect(() => {
    attemptRef.current += 1;
    return () => { attemptRef.current += 1; };
  }, [mode]);
  const { challenge, challengeView, widgetContainerRef } = useTurnstileChallenge({
    enabled: mode === 'login', siteKey: SITE_KEY, action: 'partner_login',
  });

  // WHATSAPP-OTP-STANDARD-001: Code-Schritt der SMS-Zwei-Faktor-Anmeldung (null = Passwort-Schritt).
  const [twoFactor, setTwoFactor] = useState(null);
  const [code, setCode] = useState('');
  const resendIn = useSecondsUntil(twoFactor?.resendAt);

  // Jede /login-Anfrage — Passwort, Code, erneut senden, „Lieber per SMS" — verbraucht ein FRISCHES
  // Turnstile-Token (beginSubmit verbraucht es synchron; loginFailed baut das Widget neu auf).
  const attemptLogin = async (extra = null) => {
    if (challenge.snapshot().submitting) return;   // Doppelklick: die laufende Anfrage gewinnt
    const attempt = challenge.beginSubmit();
    if (!attempt) {
      toast.error(SITE_KEY ? 'Bitte die Bot-Prüfung abschließen.' : 'Bot-Prüfung ist derzeit nicht verfügbar. Bitte später erneut versuchen.');
      return;
    }
    const sequence = ++attemptRef.current;
    const current = () => sequence === attemptRef.current;
    try {
      if (await partnerLogin(form.email, form.password, attempt.token, extra, current) === false || !current()) return;
      challenge.loginSucceeded();
      toast.success('Willkommen!');
      navigate('/');
    } catch (err) {
      if (!current()) return;
      challenge.loginFailed(err.response?.status);
      const data = err.response?.data;
      const next = twoFactorFromError(err);
      if (next) {
        setTwoFactor(next);
        return;
      }
      if (data?.error === 'turnstile_failed') {
        toast.error('Bot-Prüfung konnte nicht bestätigt werden. Bitte erneut prüfen und anmelden.');
      } else if (data?.error === 'sms_2fa_invalid') {
        setCode('');
        toast.error(data.message || TWO_FACTOR_TEXT.invalid);
      } else {
        toast.error(data?.message || data?.error || 'Anmeldung fehlgeschlagen');
      }
    }
  };
  const submit = (e) => {
    e.preventDefault();
    attemptLogin(twoFactor ? { sms_code: code.trim() } : null);
  };
  const backToPassword = () => { setTwoFactor(null); setCode(''); };

  const loading = challengeView.submitting;
  const retryMessage = {
    expired: 'Die Bot-Prüfung ist abgelaufen. Bitte erneut bestätigen.',
    'widget-error': 'Die Bot-Prüfung konnte nicht geladen werden. Bitte erneut versuchen.',
    'login-failed': twoFactor ? TWO_FACTOR_TEXT.turnstile : 'Für den nächsten Anmeldeversuch ist eine neue Bot-Prüfung erforderlich.',
    'manual-retry': 'Bitte die Bot-Prüfung erneut bestätigen.',
  }[challengeView.retryReason];
  const canAct = challengeView.canSubmit;

  return (
    <div className="pp-auth-shell is-login" style={{ minHeight: '100vh', background: '#f4f5f6', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div className="pp-auth-column" style={{ width: '100%', maxWidth: 420 }}>
        <div className="pp-auth-brand" style={{ textAlign: 'center', marginBottom: 32 }}>
          <h1 style={{ fontSize: 44, fontWeight: 900, letterSpacing: -2, marginBottom: 4 }}>V<span style={{ color: '#9C27B0' }}>OO</span>GO</h1>
          <p style={{ color: '#4b5563', fontSize: 14, fontWeight: 500 }}>Partner Portal · Mietwagenunternehmer</p>
        </div>
        <div className="pp-auth-card" style={{ background: '#fff', borderRadius: 16, padding: 32, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
          {mode === 'login' ? (
            <>
              <h2 style={{ fontSize: 16, fontWeight: 800, marginBottom: twoFactor ? 8 : 22, color: '#111' }}>{twoFactor ? TWO_FACTOR_TEXT.title : 'Anmelden'}</h2>
              <form onSubmit={submit}>
                {/* Gleiche Position im Baum wie die Passwortfelder: das Turnstile-Widget darunter bleibt montiert. */}
                {twoFactor ? (
                  <div style={{ marginBottom: 14 }}>
                    <p data-testid="two-factor-hint" style={{ color: '#4b5563', fontSize: 13, lineHeight: 1.6, margin: '0 0 12px' }}>
                      {twoFactorHint(twoFactor)}
                      {twoFactor.target && <><br /><span style={{ color: '#111', fontWeight: 700 }}>an {twoFactor.target}</span></>}
                    </p>
                    <label style={labelStyle}>Code</label>
                    <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} required autoFocus
                      inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="123456"
                      style={{ ...inputStyle, letterSpacing: 6, textAlign: 'center', fontSize: 16 }}
                      onFocus={e => e.target.style.borderColor = '#000'} onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                  </div>
                ) : [{ key: 'email', label: 'E-Mail', type: 'email', ph: 'firma@example.de' }, { key: 'password', label: 'Passwort', type: 'password', ph: '••••••••' }].map(({ key, label, type, ph }) => (
                  <div key={key} style={{ marginBottom: 14 }}>
                    <label style={labelStyle}>{label}</label>
                    <input type={type} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} placeholder={ph} required
                      style={inputStyle}
                      onFocus={e => e.target.style.borderColor = '#000'} onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                  </div>
                ))}
                {SITE_KEY ? <div ref={widgetContainerRef} style={{ marginBottom: 10 }} /> : (
                  <p role="alert" style={{ margin: '4px 0 10px', color: '#b91c1c', fontSize: 12 }}>
                    Bot-Prüfung ist derzeit nicht verfügbar. Anmeldung ist vorübergehend gesperrt.
                  </p>
                )}
                {retryMessage && (
                  <div style={{ margin: '0 0 10px', color: '#4b5563', fontSize: 12 }}>
                    {retryMessage}{' '}
                    {!loading && <button type="button" onClick={() => challenge.retryWidget()} style={{ border: 0, padding: 0, background: 'none', color: '#111', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>Neu laden</button>}
                  </div>
                )}
                <button type="submit" disabled={!challengeView.canSubmit}
                  style={{ width: '100%', padding: '12px', background: challengeView.canSubmit ? '#000' : '#e5e7eb', color: challengeView.canSubmit ? '#fff' : '#6b7280', borderRadius: 8, fontSize: 14, fontWeight: 800, marginTop: 6, border: 'none', cursor: challengeView.canSubmit ? 'pointer' : 'not-allowed' }}>
                  {twoFactor ? (loading ? TWO_FACTOR_TEXT.submitting : TWO_FACTOR_TEXT.submit) : (loading ? 'Wird angemeldet...' : 'Anmelden')}
                </button>
              </form>
              {twoFactor ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginTop: 14 }}>
                  <button type="button" onClick={() => attemptLogin(resendExtra(twoFactor))} disabled={!canAct || resendIn > 0} style={linkButton(canAct && resendIn === 0)}>
                    {resendIn > 0 ? `${TWO_FACTOR_TEXT.resend} (in ${resendIn} s)` : TWO_FACTOR_TEXT.resend}
                  </button>
                  {twoFactor.channel === 'whatsapp' && (
                    <button type="button" onClick={() => attemptLogin({ channel: 'sms' })} disabled={!canAct} title={OTP_TEXT.preferSmsHint} style={linkButton(canAct)}>
                      {OTP_TEXT.preferSms}
                    </button>
                  )}
                  <button type="button" onClick={backToPassword} disabled={loading} style={linkButton(!loading)}>
                    {TWO_FACTOR_TEXT.back}
                  </button>
                </div>
              ) : (<>
              <div style={{ textAlign: 'center', marginTop: 14 }}>
                <button onClick={() => { challenge.abandonWidget(); setMode('forgot'); }} disabled={loading} style={{ background: 'none', border: 'none', color: '#4b5563', fontSize: 13, cursor: loading ? 'not-allowed' : 'pointer', textDecoration: 'underline' }}>
                  Passwort vergessen?
                </button>
              </div>
              <p style={{ textAlign: 'center', marginTop: 12, color: '#6b7280', fontSize: 13 }}>
                Noch kein Konto? <Link to="/register" style={{ color: '#000', fontWeight: 700 }}>Registrieren</Link>
              </p>
              </>)}
            </>
          ) : (
            <ForgotPassword email={form.email} onBack={() => setMode('login')} />
          )}
        </div>
        <p style={{ textAlign: 'center', marginTop: 16, color: '#d1d5db', fontSize: 12 }}>
          VOOGO UG (haftungsbeschränkt) · Partner-Vermittlung gem. § 49 PBefG
        </p>
      </div>
    </div>
  );
}

// Passwort vergessen (Partner): E-Mail → Code (WhatsApp/SMS) ODER E-Mail → Code + neues Passwort.
function ForgotPassword({ email: initialEmail, onBack }) {
  const [step, setStep] = useState('email');   // 'email' | 'verify'
  const [email, setEmail] = useState(initialEmail || '');
  const [dest, setDest] = useState(null);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const active = useRef(false);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const otp = useOtpDelivery();   // WHATSAPP-OTP-STANDARD-001: wohin der Telefon-Code ging

  // `preferSms`: „Lieber per SMS" — dieselbe Anfrage mit channel:'sms', damit der noch gültige
  // WhatsApp-Code einmal per SMS kommt.
  const requestVia = async (via, { preferSms = false } = {}) => {
    if (!email) { toast.error('Bitte E-Mail eingeben.'); return false; }
    setBusy(true);
    try {
      const { data: r, channel } = await sendJson('/auth/forgot/request', preferSms ? { email, via, channel: 'sms' } : { email, via });
      if (!active.current) return false;
      setDest(r.dest);
      if (r.sent === true) {
        otp.receive(via === 'email' ? null : channel);
        toast.success(via === 'email' ? `Code per E-Mail an ${r.dest} gesendet.` : `Code per ${otpChannelLabel(channel)} an ${r.dest} gesendet.`);
        setStep('verify');
        return true;
      }
      toast.error(via === 'email' ? 'E-Mail-Versand ist (noch) nicht verfügbar.' : 'SMS-Versand ist (noch) nicht verfügbar.');
    } catch (err) { if (!active.current) return; toast.error(err.message || 'Fehler'); }
    finally { if (active.current) setBusy(false); }
    return false;
  };
  const preferSms = async () => {
    if (busy || !otp.beginSmsSwitch()) return;   // Doppelklick-Schutz
    if (!(await requestVia('sms', { preferSms: true }))) otp.failSmsSwitch();
  };

  const verify = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { toast.error('Neues Passwort: mindestens 8 Zeichen.'); return; }
    setBusy(true);
    try {
      await postJson('/auth/forgot/verify', { email, newPassword, code });
      if (!active.current) return;
      toast.success('Passwort gesetzt. Bitte jetzt anmelden.');
      onBack();
    } catch (err) { if (!active.current) return; toast.error(err.message || 'Code ungültig.'); }
    finally { if (active.current) setBusy(false); }
  };

  const greenBtn = { width: '100%', padding: '11px', background: '#9C27B0', color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 800, border: 'none', cursor: 'pointer', marginTop: 8 };

  return (
    <>
      <h2 style={{ fontSize: 16, fontWeight: 800, marginBottom: 8, color: '#111' }}>Passwort zurücksetzen</h2>
      {step === 'email' ? (
        <div>
          <p style={{ color: '#4b5563', fontSize: 13, lineHeight: 1.6, marginBottom: 14 }}>
            Gib deine E-Mail ein und wähle, wohin wir den Code senden sollen.
          </p>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>E-Mail</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="firma@example.de" autoFocus style={inputStyle} />
          </div>
          <button onClick={() => requestVia('sms')} disabled={busy} style={greenBtn}>Code</button>
          <button onClick={() => requestVia('email')} disabled={busy} style={{ ...greenBtn, background: '#111' }}>Code per E-Mail</button>
        </div>
      ) : (
        <form onSubmit={verify}>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Code{dest ? ` (an ${dest})` : ''}</label>
            {otp.channel && (
              <p style={{ color: '#4b5563', fontSize: 13, lineHeight: 1.6, margin: '0 0 8px' }}>{otpSentText(otp.channel)}</p>
            )}
            <input inputMode="numeric" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} placeholder="Code eingeben"
              style={{ ...inputStyle, letterSpacing: 4, textAlign: 'center' }} />
            {otp.showsPreferSms && (
              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <button type="button" onClick={preferSms} disabled={busy || otp.switching} title={OTP_TEXT.preferSmsHint}
                  style={{ background: 'none', border: 'none', color: '#4b5563', fontSize: 13, cursor: busy || otp.switching ? 'not-allowed' : 'pointer', textDecoration: 'underline' }}>
                  {OTP_TEXT.preferSms}
                </button>
              </div>
            )}
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Neues Passwort (min. 8 Zeichen)</label>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" required style={inputStyle} />
          </div>
          <button type="submit" disabled={busy} style={greenBtn}>{busy ? 'Bitte warten…' : 'Passwort setzen'}</button>
        </form>
      )}
      <div style={{ textAlign: 'center', marginTop: 14 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#4b5563', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>
          ← Zurück zur Anmeldung
        </button>
      </div>
    </>
  );
}
